import { useEffect, useState } from "react";
import { Link } from "react-router";

import { Destination, FormField } from "@prequel/react";
import { SearchableDropdown, SearchableDropdownListItem } from "@prequel/shared-components";
import { AlertCircleIcon } from "@prequel/shared-components";

import { WrapperProps } from "..";
import InputWrapper from "../InputWrapper";

import { useAllRecipients } from "@/api/queries/recipients";
import { MagicLink } from "@/types";
import { Source } from "@/types/sources";
import { SourceFormField } from "@/types/sources/form";

const InternalWrapper = <F extends FormField | SourceFormField, C extends Destination | Source | MagicLink>({
  field,
  connection,
  setConnectionField,
  disabled,
  isEditing,
}: WrapperProps<F, C>) => {
  const [recipientOptions, setRecipientOptions] = useState<SearchableDropdownListItem<string>[]>();

  const { data: recipients } = useAllRecipients();

  useEffect(() => {
    if (recipients) {
      const options = recipients.map((recipient) => ({
        key: recipient.id,
        text: (
          <span className="flex items-center">
            <span className="mr-1">{recipient.name}</span>
            <span className="truncate text-xs text-gray-500">({recipient.id_in_provider_system})</span>
          </span>
        ),
        searchValue: `${recipient.name} ${recipient.id_in_provider_system}`,
      }));

      setRecipientOptions(options);
    }
  }, [recipients]);

  if (field.name === "name" && field.form_element === "input") {
    return (
      <InputWrapper
        field={field as F & { form_element: "input" }}
        connection={connection}
        setConnectionField={setConnectionField}
        disabled={disabled}
        isEditing={isEditing}
      />
    );
  }

  if (field.name === "recipient_id" && "recipient_id" in connection) {
    const selectedRecipient = recipientOptions?.find(({ key }) => key === connection.recipient_id);

    return (
      <>
        {recipientOptions && recipientOptions.length ? (
          <>
            <SearchableDropdown
              label={field.label}
              items={recipientOptions ?? []}
              selectedItem={selectedRecipient}
              setSelectedItem={({ key }) => setConnectionField("recipient_id" as keyof C, key)}
              disabled={disabled || isEditing}
              subtext="Recipient name (id_in_provider_system)"
            />
          </>
        ) : (
          <div className="flex-col rounded-sm border border-gray-200 bg-gray-50 p-3 text-sm text-gray-500">
            <div className="flex">
              <AlertCircleIcon className="size-5 text-emerald-600" aria-hidden="true" />
              <div className="ml-1">
                <p className="text-sm text-gray-900">No configured recipients.</p>
                <p className="text-xs">
                  <Link
                    className="
                      text-emerald-600
                      hover:text-emerald-800
                    "
                    to="/recipients/new"
                    target="_blank"
                  >
                    Create a recipient here
                  </Link>{" "}
                  to add your first.
                </p>
              </div>
            </div>
          </div>
        )}
      </>
    );
  }

  // Skip any field that we haven't special-cased
  return <></>;
};

export default InternalWrapper;
